'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { tryParseAsNumber } from '@leid/helpers'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { BrowsePageSearchParams } from 'app/(pages)/browse/models'
import { trackProductSearch } from 'app/components/shared/Analytics/segment'
import { convertSerachParamsToURLSearchParams } from 'app/utils/urls'
import { parseRouterQuery } from 'utils/parseRouterQuery'

import { InputWithButton } from '../InputWithButton'

type Props = {
  showDropdown?: boolean
  onSearchCallback?: () => void
  redirectTo: string
}

export const SearchForProducts: React.FC<Props> = ({ onSearchCallback, redirectTo }) => {
  const { t } = useTranslation(['common'])
  const router = useRouter()
  const query = useSearchParams()
  const pathname = usePathname()

  const querySearchTerm = parseRouterQuery(query?.get('searchTerm') ?? undefined)

  const [searchTerm, setSearchTerm] = useState(querySearchTerm)

  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleSearch = async () => {
    if (searchTerm === querySearchTerm) {
      return
    }

    if (onSearchCallback) {
      onSearchCallback()
    }

    const newQuery: BrowsePageSearchParams = {
      searchTerm: searchTerm,
      location: tryParseAsNumber(query?.get('location')),
      manager: tryParseAsNumber(query?.get('manager')),
      page: undefined,
      category: undefined,
      rentedFrom: undefined,
      rentedTo: undefined,
    }

    await trackProductSearch(searchTerm, pathname ?? '')

    const params = convertSerachParamsToURLSearchParams(newQuery)
    router.push(`/${redirectTo}?${params.toString()}`)
  }

  useEffect(() => {
    if (searchTerm !== querySearchTerm) {
      setSearchTerm(querySearchTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySearchTerm])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSearch()

        if (inputRef && inputRef.current) {
          inputRef.current.blur()
        }
      }}
    >
      <InputWithButton
        placeholder={t('common:searchInputPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        name="searchProduct"
        onClick={handleSearch}
        inputRef={inputRef}
      />
    </form>
  )
}
